/**
 * Modo demonstração do onboarding do trial (`?onboardingPreview=1`).
 * Liberado pela flag `TrialOnboardingPreview` (Unleash, segmentada por usuário).
 * Não grava nada no servidor: o modo fica no sessionStorage e o progresso no localStorage.
 */
import type { GuiaTrialResult } from "@/services/eventos-trial.service";

export const ONBOARDING_PREVIEW_PARAM = "onboardingPreview";
const SESSAO_KEY = "sysigreja:onboarding-preview";
const PROGRESSO_KEY = "sysigreja:onboarding-preview:progresso";

type ItemManual = "tour" | "link" | "pagina" | "checkin";
type Progresso = { boasVindas?: string; marcos: Partial<Record<ItemManual, string>> };

/**
 * Resultado da flag para o usuário logado. "pendente" enquanto o Unleash não respondeu com o
 * `userId` no context: antes disso a flag segmentada vem desligada e o modo piscava.
 */
export type AvaliacaoOnboardingPreview = "pendente" | boolean;

let avaliacao: AvaliacaoOnboardingPreview = "pendente";
let versao = 0;
const ouvintes = new Set<() => void>();

function avisar() {
  versao += 1;
  ouvintes.forEach((fn) => fn());
}

export function assinarOnboardingPreview(fn: () => void) {
  ouvintes.add(fn);
  return () => {
    ouvintes.delete(fn);
  };
}

export function versaoOnboardingPreview() {
  return versao;
}

function lerStorage(storage: Storage | undefined, key: string) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function gravarStorage(storage: Storage | undefined, key: string, valor: string | null) {
  try {
    if (valor === null) storage?.removeItem(key);
    else storage?.setItem(key, valor);
  } catch {
    // storage indisponível (modo privado etc.): o modo demo só não persiste
  }
}

const sessao = () => (typeof window !== "undefined" ? window.sessionStorage : undefined);
const local = () => (typeof window !== "undefined" ? window.localStorage : undefined);

/** Chamado só pelo hook do App (fonte única), com a flag já avaliada para o usuário. */
export function definirAvaliacaoOnboardingPreview(valor: AvaliacaoOnboardingPreview) {
  if (avaliacao === valor) return;
  avaliacao = valor;
  avisar();
}

/** Atalho: flag já avaliada para o usuário (true/false). */
export function definirFlagOnboardingPreview(liberada: boolean) {
  definirAvaliacaoOnboardingPreview(liberada);
}

/** Resposta do Unleash e o `userId` do context com que ela foi calculada. */
export type RespostaFlagOnboardingPreview = { userId: string; liberada: boolean };

/**
 * Regra determinística: só vale a resposta calculada com o `userId` do usuário logado.
 * Resposta sem userId, de outro usuário ou do cache local não conta (fica "pendente").
 * Erro do Unleash sem resposta válida encerra a espera como desligado.
 */
export function resolverAvaliacaoOnboardingPreview(params: {
  userId: string | null | undefined;
  resposta: RespostaFlagOnboardingPreview | null;
  erroUnleash: boolean;
}): AvaliacaoOnboardingPreview {
  const userId = String(params.userId ?? "").trim();
  const { resposta } = params;
  if (userId && resposta && resposta.userId === userId) return resposta.liberada;
  if (params.erroUnleash) return false;
  return "pendente";
}

/**
 * Aplica `?onboardingPreview=1|0`. O pedido é guardado no sessionStorage mesmo antes de a flag
 * carregar (o Unleash responde depois do login, e o redirect inicial do /sistema perde a query);
 * o modo só fica ativo com a flag avaliada e liberada para o usuário.
 */
export function aplicarParametroOnboardingPreview(search: string) {
  const valor = new URLSearchParams(search).get(ONBOARDING_PREVIEW_PARAM);
  if (valor === null) return;
  const novo = valor === "1" ? "1" : null;
  if (lerStorage(sessao(), SESSAO_KEY) === novo) return;
  gravarStorage(sessao(), SESSAO_KEY, novo);
  avisar();
}

function pedidoNaSessao() {
  return lerStorage(sessao(), SESSAO_KEY) === "1";
}

export function isOnboardingPreviewAtivo() {
  return avaliacao === true && pedidoNaSessao();
}

/** Pediu o modo e a flag ainda não foi avaliada com o usuário: a Home segura a decisão. */
export function isOnboardingPreviewPendente() {
  return avaliacao === "pendente" && pedidoNaSessao();
}

// Captura a query já no carregamento do bundle, antes de qualquer redirect do router.
if (typeof window !== "undefined") aplicarParametroOnboardingPreview(window.location.search);

function lerProgresso(): Progresso {
  const bruto = lerStorage(local(), PROGRESSO_KEY);
  if (!bruto) return { marcos: {} };
  try {
    const parsed = JSON.parse(bruto) as Progresso;
    return { boasVindas: parsed.boasVindas, marcos: parsed.marcos ?? {} };
  } catch {
    return { marcos: {} };
  }
}

function gravarProgresso(progresso: Progresso) {
  gravarStorage(local(), PROGRESSO_KEY, JSON.stringify(progresso));
  avisar();
}

export function marcarItemPreview(item: ItemManual) {
  const progresso = lerProgresso();
  if (progresso.marcos[item]) return;
  progresso.marcos[item] = new Date().toISOString();
  gravarProgresso(progresso);
}

export function boasVindasPreviewVista() {
  return Boolean(lerProgresso().boasVindas);
}

export function marcarBoasVindasPreview(valor?: string) {
  gravarProgresso({ ...lerProgresso(), boasVindas: valor ?? "visto" });
}

/** Guia falso, em memória, com o progresso do localStorage. */
export function guiaPreview(eventoId?: number | null): GuiaTrialResult {
  return {
    eventoId: Number(eventoId) || 0,
    configuracaoId: null,
    publicado: true,
    dataEvento: null,
    dataFim: null,
    encerrado: false,
    inscritos: 0,
    mensagens: 0,
    cupons: 0,
    crachas: 0,
    marcos: { ...lerProgresso().marcos },
  };
}

export function recomecarOnboardingPreview() {
  gravarStorage(local(), PROGRESSO_KEY, null);
  avisar();
}

export function sairOnboardingPreview() {
  gravarStorage(sessao(), SESSAO_KEY, null);
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href);
    if (url.searchParams.has(ONBOARDING_PREVIEW_PARAM)) {
      url.searchParams.delete(ONBOARDING_PREVIEW_PARAM);
      window.history.replaceState(window.history.state, "", url.toString());
    }
  }
  avisar();
}
